import styled from 'styled-components';

// Define main colors as variables
const COLORS = {
  rewards: '#88BA1F', // Green
  consequences: '#EF4444', // Red
  obstacles: '#F59E0B', // Orange
  solutions: '#6366F1', // Blue
  default: '#000000', // Default black
};

// Helper function to convert hex to rgba
const hexToRgba = (hex: string, alpha: number): string => {
  // Remove the hash if it exists
  const cleanHex = hex.replace('#', '');
  
  // Parse the hex values
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  
  // Return rgba value
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Get color based on type
const getColor = (type: string): string => {
  return COLORS[type as keyof typeof COLORS] || COLORS.default;
};

export const EditButton = styled.button`
  background-color: transparent;
  border: none;
  color: #1271A6;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  padding: 0;
  opacity: 0;
  transition: opacity 0.2s ease;
`;

export const Wrapper = styled.div<{ type: string; isEditing?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 22px 24px;
  border-radius: 12px;
  border: 1px solid transparent;
  gap: 16px;
  height: 100%;
  transition: background-color 0.3s, border-color 0.3s;
  background-color: ${({ type, isEditing }) => isEditing ? hexToRgba(getColor(type), 0.04) : 'transparent'};
  border-color: ${({ type, isEditing }) => isEditing ? hexToRgba(getColor(type), 0.12) : 'transparent'};

  &:hover {
    background-color: ${({ type }) => hexToRgba(getColor(type), 0.04)};
    border-color: ${({ type }) => hexToRgba(getColor(type), 0.12)};
    
    ${EditButton} {
      opacity: 1;
    }
  }
  
  ${({ isEditing }) => isEditing && `
    ${EditButton} {
      opacity: 1;
    }
  `}
  
  ${({ theme, type }) => theme.max("md")`
    background-color: ${hexToRgba(getColor(type), 0.04)};
    border-color: ${hexToRgba(getColor(type), 0.12)};
    ${EditButton} {
      opacity: 1;
    }
  `}
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const ColumnLeft = styled.div`
  display: flex;
  align-items: center;
  text-align: left;
  gap: 12px;
`;

export const ColumnRight = styled.div`
  display: flex;
  align-items: center;
`;

export const IconWrapper = styled.div<{ type: string }>`
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: ${({ type }) => hexToRgba(getColor(type), 0.125)};
  margin-right: 8px;
`;

export const Icon = styled.img`
  width: 16px;
  height: 16px;
`;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const MainText = styled.span<{ type: string }>`
  color: ${({ type }) => getColor(type)};
  font-family: Inter;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
`;

export const SubText = styled.span`
  color: #64748B;
  font-family: Inter;
  font-size: 12px;
  line-height: 18px;
`;

export const CancelButton = styled.button`
  background-color: transparent;
  border: none;
  color: #64748B;
  font-family: Inter;
  font-size: 14px;
  line-height: 21px;
  padding: 0;
`;

export const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const Body = styled.div<{ isEditing?: boolean }>`
  padding: ${({ isEditing }) => isEditing ? '0 0 10px 0' : '10px 16px'};
`;

export const BodyText = styled.p`
  color: #334155;
  font-family: Inter;
  font-size: 16px;
  line-height: 25.6px;
  margin: 0;
  text-align: left;
  word-break: break-word;
`;

export const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 2px;
  border: 1px solid #1271A6;
`;
